import { Infobox } from "@npco/zeller-design-system";
import { bindField, useForm } from "@npco/zeller-design-system/form";
import * as Input from "@npco/zeller-design-system/input";
import { useMemo } from "react";
import { twJoin } from "tailwind-merge";
import { z } from "zod";

import { formatCurrency } from "../../../../@lib-i18n";
import { Layout } from "../../shared/layout";
import type { Transaction } from "./get-transaction";
import { RefundTransactionsList } from "./refund-transactions-list";
import { defaultLayoutProps } from "./render";
import { TransactionsListItemDisplay } from "./transactions-list/transactions-list-item";

export type RefundTransactionState = {
	onCancel: () => void;
	onRefund: (refundAmount: number) => void;
	refundAmount: number;
	transaction: Transaction;
	isExternalMatch?: boolean;
};

const getValuesSubmitSchema = ({
	balanceAmount,
	currency,
}: {
	balanceAmount: number;
	currency?: string | null;
}) =>
	z.discriminatedUnion("refundType", [
		z.object({
			refundType: z.literal("full"),
			amountInput: z.any(),
		}),
		z.object({
			refundType: z.literal("partial"),
			amountInput: z
				.number({ message: "Enter a valid Refund Amount" })
				.positive({ message: "Refund Amount must be greater than $0.00" })
				.lte(balanceAmount, {
					message: `Maximum Refund Amount is ${formatCurrency({
						amountInCents: balanceAmount,
						currency: currency ?? undefined,
					})}. Please enter a lower value.`,
				}),
		}),
	]);
export const RefundTransaction = ({
	onCancel,
	onRefund,
	refundAmount,
	transaction,
	isExternalMatch,
}: RefundTransactionState) => {
	const {
		amount: transactionAmount,
		currency: transactionCurrency,
		refundedAmount,
	} = transaction;
	const balanceAmount = transactionAmount - (refundedAmount ?? 0);
	const currency = transactionCurrency ?? undefined;

	const schema = useMemo(
		() => getValuesSubmitSchema({ balanceAmount, currency }),
		[balanceAmount, currency],
	);
	const form = useForm(
		schema,
		({ value: { amountInput, refundType } }) => {
			if (refundType === "full") {
				onRefund(balanceAmount);
				return;
			}

			onRefund(amountInput);
		},
		{
			validators: {
				onMount: schema as unknown as undefined,
				onChange: schema as unknown as undefined,
				onBlur: schema as unknown as undefined,
			},
			defaultValues: {
				amountInput: refundAmount,
				refundType: refundAmount === balanceAmount ? "full" : "partial",
			},
		},
	);

	const isAlreadyRefunded = balanceAmount === 0;
	const isPartiallyRefunded = balanceAmount < transactionAmount;
	const hasRefunds = !!transaction.refundTransactions?.length;

	return (
		<form.Subscribe
			selector={(state) => ({
				amountInput: state.values.amountInput,
				refundType: state.values.refundType,
				canSubmit: state.canSubmit,
			})}
		>
			{({ amountInput, refundType, canSubmit }) => {
				const isFullRefund = refundType === "full";
				const refundAmount = isFullRefund ? balanceAmount : (amountInput ?? 0);
				return (
					<Layout
						{...defaultLayoutProps}
						title="Refund Transaction"
						actions={[
							...(!isAlreadyRefunded
								? [
										{
											label: refundAmount
												? `Refund ・ ${formatCurrency({ amountInCents: refundAmount, currency })}`
												: "Refund",
											onClick: () => {
												void form.handleSubmit();
											},
											primary: true,
											disabled: !canSubmit,
										},
									]
								: []),
							{
								label: isExternalMatch ? "Back" : "Cancel",
								onClick: onCancel,
								primary: isAlreadyRefunded,
							},
						]}
					>
						<div className="flex flex-col gap-16 px-32">
							<TransactionsListItemDisplay readOnly {...transaction} />
							{isAlreadyRefunded && (
								<Infobox message="This transaction has been fully refunded." />
							)}
							<fieldset>
								<form.Field name="refundType">
									{(field) => (
										<div className="mb-16 flex w-full flex-col gap-16">
											<label
												className={twJoin(
													"flex",
													"items-center",
													"gap-8",
													"rounded-8",
													"px-16 py-12",
													"cursor-pointer",
													"has-[:disabled]:cursor-not-allowed",
													"has-[:checked]:bg-blue-80",
													"has-[:checked]:dark:bg-blue-5000",
													"has-[:enabled]:hover:bg-grey-20",
													"has-[:enabled]:hover:dark:bg-grey-900",
												)}
											>
												<input
													className="h-[20px] w-[20px] disabled:pointer-events-none"
													defaultChecked={isFullRefund}
													disabled={isAlreadyRefunded}
													type="radio"
													{...bindField(field)}
													onChange={() => {
														field.handleChange("full");
													}}
												/>
												Full Refund
											</label>
											<label
												className={twJoin(
													"flex",
													"items-center",
													"gap-8",
													"rounded-8",
													"px-16 py-12",
													"cursor-pointer",
													"has-[:disabled]:cursor-not-allowed",
													"has-[:checked]:bg-blue-80",
													"has-[:checked]:dark:bg-blue-5000",
													"has-[:enabled]:hover:bg-grey-20",
													"has-[:enabled]:hover:dark:bg-grey-900",
												)}
											>
												<input
													className="h-[20px] w-[20px] disabled:pointer-events-none"
													defaultChecked={!isFullRefund}
													disabled={isAlreadyRefunded}
													type="radio"
													{...bindField(field)}
													onChange={() => field.handleChange("partial")}
												/>
												Partial Refund
											</label>
										</div>
									)}
								</form.Field>

								{(refundType === "partial" || isPartiallyRefunded) && (
									<div className="flex w-full items-baseline justify-between gap-4">
										<span className="mt-8 text-lg/heading">
											Refundable Balance
										</span>
										<span>
											{formatCurrency({
												amountInCents: balanceAmount,
												currency,
											})}
										</span>
									</div>
								)}
								{refundType === "partial" && (
									<form.Field name="amountInput">
										{(field) => (
											<Input.Currency
												className="pt-16"
												currency={
													currency && Input.Currency.isCode(currency)
														? currency
														: "AUD"
												}
												label="Refund Amount"
												{...bindField(field)}
												// TanStack resets an undefined field to its default on render, so hold empty as null.
												value={field.state.value ?? undefined}
												onChange={(value) => field.handleChange(value ?? null)}
											/>
										)}
									</form.Field>
								)}
							</fieldset>

							{hasRefunds && transaction.refundTransactions && (
								<div>
									<hr className="my-8 text-grey-90 dark:text-grey-800" />
									<div className="pb-16 pt-8 text-lg/heading">
										Previous Refunds
									</div>
									<RefundTransactionsList
										transactions={transaction.refundTransactions}
									/>
								</div>
							)}
						</div>
					</Layout>
				);
			}}
		</form.Subscribe>
	);
};
